import React from 'react';
import { getMetricShortName } from '@src/utils/metrics';
import type { MenuProps } from 'antd';
import { SeverityLabel, StyledChildren, StyledEllipsisTooltip, StyledLabel } from './styled';
import { CURRENT_PRODUCT_MODE, isAppModule } from '@src/utils/constants';
import metricsStore from '@src/globalStore/metricsStore';
import {
  AI_ALERT_KPI_BETA_METRICS,
  AI_ALERT_STANDARD_METRICS_MAP,
  DELETED_METRIC,
} from '../constants';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import _, { isEmpty } from 'lodash';
import { FLOW_METRICS_DISPLAY } from '@src/utils/flow/meta';

export const STATIC_SIDEBAR_FILTERS = {
  ALL_ALERTS: 'ALL_ALERTS',
  CRITICAL: 'Critical',
  WARNING: 'Warning',
  INFO: 'Info',
  VIDEO_ALERT: 'VIDEO_ALERT',
  AD_ALERT: 'AD_ALERT',
  ACTIVE: 'Active',
};

export const SEVERITY_CONFIG = {
  Critical: {
    key: 'Critical',
    color: '#D0021B',
    label: <SeverityLabel color="#D0021B">Critical</SeverityLabel>,
    icon: <WarningOutlined style={{ color: '#D0021B', fontSize: '14px' }} />,
    order: 3,
  },
  Warning: {
    key: 'Warning',
    color: '#FF9300',
    label: <SeverityLabel color="#FF9300">Warning</SeverityLabel>,
    icon: <ExclamationCircleOutlined style={{ color: '#FF9300' }} />,
    order: 2,
  },
  Info: {
    key: 'Info',
    color: '#788BA9',
    label: <SeverityLabel color="#788BA9">Info</SeverityLabel>,
    icon: <InfoCircleOutlined style={{ color: '#788BA9' }} />,
    order: 1,
  },
  Ended: {
    key: 'Ended',
    color: '#4A4A4A',
    label: <SeverityLabel color="#4A4A4A">Ended</SeverityLabel>,
    icon: <ClockCircleOutlined style={{ color: '4A4A4A', marginRight: '3px' }} />,
    order: 0,
  },
};

export const getSearchedAlerts = ({ alerts, searchText }) => {
  return alerts.filter((record) => {
    // Search across all fields of the row (Alert ID, Root Cause, Metric Name, etc.)
    const searchFields = [record.alertId.toString(), record.rootCauseDisplay];

    return searchFields.some((field) =>
      field.toString().toLowerCase().includes(searchText.toLowerCase()),
    );
  });
};

export const getFilteredAlertsByCategory = ({ alerts, filters }) => {
  return filters.length
    ? alerts?.filter((record) => {
        const filterFields = [
          record.alertType,
          record.peakSeverity,
          record.metricName,
          record.status,
        ];

        return filterFields.some((field) => filters.includes(field.toString()));
      })
    : alerts;
};

export const getMetricAlertsCountMap = (alerts) =>
  alerts.reduce((acc, alert) => {
    // If category is null or undefined, replace it with '[DELETED_METRIC]'
    const category = alert.metricDisplayName === DELETED_METRIC ? DELETED_METRIC : alert.metricName;

    // Increment the count for the category
    if (!acc[category]) {
      acc[category] = 0;
    }
    acc[category]++;

    return acc;
  }, {});

export const getEcoMetricCategoryMap = (alerts) => {
  const metricAlertsCountMap: any = {
    'Flow Metrics': {},
    'Custom Metrics': [],
    'Standard Metrics': [],
  };
  const { userDefinedMetricsMeta, flowConfigMeta } = metricsStore.getState();
  const customMetrics = userDefinedMetricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];

  const categoryMap = getMetricAlertsCountMap(alerts);

  for (const metricKey in categoryMap) {
    const metricAlertsCount = categoryMap[metricKey];
    if (metricAlertsCount) {
      if (customMetrics?.[metricKey]) {
        const flowId = customMetrics[metricKey].flowID;
        if (flowId) {
          const invertFlowMetricMap = _.invert(flowConfigMeta[flowId]?.metrics);
          const flowName = flowConfigMeta[flowId].name;
          metricAlertsCountMap['Flow Metrics'][flowName] = [
            ...(metricAlertsCountMap['Flow Metrics'][flowName] || []),
            {
              metric: metricKey,
              count: metricAlertsCount,
              label: FLOW_METRICS_DISPLAY[invertFlowMetricMap[metricKey]]?.name,
            },
          ];
        } else
          metricAlertsCountMap['Custom Metrics'].push({
            metric: metricKey,
            count: metricAlertsCount,
            label: getMetricShortName({ metricKey }),
          });
      } else
        metricAlertsCountMap['Standard Metrics'].push({
          metric: metricKey,
          count: metricAlertsCount,
          label: getMetricShortName({ metricKey }),
        });
    }
  }
  Object.keys(metricAlertsCountMap['Flow Metrics']).forEach((key) =>
    metricAlertsCountMap['Flow Metrics'][key].sort((a, b) => b.count - a.count),
  );
  metricAlertsCountMap['Custom Metrics'].sort((a, b) => b.count - a.count);
  metricAlertsCountMap['Standard Metrics'].sort((a, b) => b.count - a.count);

  return metricAlertsCountMap;
};

const addEiMetricToCategoryMap = (map, metricKey, count, isAdAlertEnabled) => {
  const entry = {
    metric: metricKey,
    count,
    label: getMetricShortName({ metricKey }),
  };
  if (AI_ALERT_KPI_BETA_METRICS.includes(metricKey)) {
    map['KPI Metrics'].push(entry);
  } else if (AI_ALERT_STANDARD_METRICS_MAP.AI.includes(metricKey) && isAdAlertEnabled) {
    map['Ad Metrics'].push(entry);
  } else if (AI_ALERT_STANDARD_METRICS_MAP.EI.includes(metricKey)) {
    map['Video Metrics'].push(entry);
  }
};

const sortVideoMetricCategoryMap = (map) => {
  map['Ad Metrics'].sort((a, b) => b.count - a.count);
  map['Video Metrics'].sort((a, b) => b.count - a.count);
  map['KPI Metrics'].sort((a, b) => b.count - a.count);
};

export const getVideoMetricCategoryMap = (alerts, isAdAlertEnabled = false) => {
  const metricAlertsCountMap: any = { 'Video Metrics': [], 'Ad Metrics': [], 'KPI Metrics': [] };

  const categoryMap = getMetricAlertsCountMap(alerts);

  for (const metricKey in categoryMap) {
    const metricAlertsCount = categoryMap[metricKey];
    if (metricAlertsCount) {
      addEiMetricToCategoryMap(
        metricAlertsCountMap,
        metricKey,
        metricAlertsCount,
        isAdAlertEnabled,
      );
    }
  }
  sortVideoMetricCategoryMap(metricAlertsCountMap);

  return metricAlertsCountMap;
};

type MenuItem = Required<MenuProps>['items'][number];

export const getSidebarConfig = ({ alerts, featureFlags }) => {
  const { isAdAlertEnabled = false } = featureFlags;
  const { ALL_ALERTS, CRITICAL, WARNING, INFO, ACTIVE, VIDEO_ALERT, AD_ALERT } =
    STATIC_SIDEBAR_FILTERS;
  const { Critical, Warning, Info } = SEVERITY_CONFIG;

  const criticalAlerts = getFilteredAlertsByCategory({ alerts, filters: [CRITICAL] });
  const warningAlerts = getFilteredAlertsByCategory({ alerts, filters: [WARNING] });
  const infoAlerts = getFilteredAlertsByCategory({ alerts, filters: [INFO] });

  const activeAlerts = getFilteredAlertsByCategory({ alerts, filters: [ACTIVE] });

  const videoAlerts = getFilteredAlertsByCategory({ alerts, filters: [VIDEO_ALERT] });
  const adAlerts = getFilteredAlertsByCategory({ alerts, filters: [AD_ALERT] });

  const metricsCategoryMap = isAppModule
    ? getEcoMetricCategoryMap(alerts)
    : getVideoMetricCategoryMap(alerts, isAdAlertEnabled);

  const items: MenuItem[] = [
    {
      key: ALL_ALERTS,
      disabled: !alerts?.length,
      label: (
        <StyledChildren disabled={false}>
          <span>All Alerts</span>
          <span>{alerts?.length}</span>
        </StyledChildren>
      ),
    },
    {
      type: 'divider',
    },
    ...(CURRENT_PRODUCT_MODE === 'EI' && isAdAlertEnabled
      ? [
          {
            key: VIDEO_ALERT,
            disabled: !videoAlerts?.length,
            label: (
              <StyledChildren disabled={!videoAlerts?.length}>
                <span>Video Alerts</span>
                <span>{videoAlerts?.length}</span>
              </StyledChildren>
            ),
          },
          {
            key: AD_ALERT,
            disabled: !adAlerts?.length,
            label: (
              <StyledChildren disabled={!adAlerts?.length}>
                <span>Ad Alerts</span>
                <span>{adAlerts?.length}</span>
              </StyledChildren>
            ),
          },
        ]
      : []),
    {
      key: ACTIVE,
      disabled: !activeAlerts?.length,
      label: (
        <StyledChildren disabled={!activeAlerts?.length}>
          <span style={{ color: '#215bff' }}>Active Alerts</span>
          {activeAlerts?.length}
        </StyledChildren>
      ),
    },

    {
      type: 'divider',
    },
    {
      ...Critical,
      key: CRITICAL,
      disabled: !criticalAlerts?.length,
      label: (
        <StyledChildren disabled={!criticalAlerts?.length}>
          {Critical.label}
          {criticalAlerts?.length}
        </StyledChildren>
      ),
    },
    {
      ...Warning,
      key: WARNING,
      disabled: !warningAlerts?.length,
      label: (
        <StyledChildren disabled={!warningAlerts?.length}>
          {Warning.label}
          {warningAlerts?.length}
        </StyledChildren>
      ),
    },
    {
      ...Info,
      key: INFO,
      disabled: !infoAlerts?.length,
      label: (
        <StyledChildren disabled={!infoAlerts?.length}>
          {Info.label}
          {infoAlerts?.length}
        </StyledChildren>
      ),
    },

    {
      type: 'divider',
    },

    ...Object.keys(metricsCategoryMap).map((key) =>
      key === 'Flow Metrics' && !isEmpty(metricsCategoryMap[key])
        ? {
            key: key,
            label: <StyledLabel>{key}</StyledLabel>,
            children: Object.keys(metricsCategoryMap[key]).map((flowKey) => ({
              key: flowKey,
              type: 'group',
              label: (
                <StyledChildren disabled={false}>
                  <StyledEllipsisTooltip tooltipText={flowKey}>{flowKey}</StyledEllipsisTooltip>
                </StyledChildren>
              ),
              children: metricsCategoryMap[key][flowKey].map(({ metric, count, label }) => ({
                key: metric,
                label: (
                  <StyledChildren disabled={!!count}>
                    <StyledEllipsisTooltip tooltipText={label}>{label}</StyledEllipsisTooltip>
                    {count}
                  </StyledChildren>
                ),
              })),
            })),
          }
        : metricsCategoryMap[key].length
          ? {
              key: key,
              label: <StyledLabel>{key}</StyledLabel>,
              children: metricsCategoryMap[key].map(({ metric, count, label }) => ({
                key: metric,
                label: (
                  <StyledChildren disabled={!!count}>
                    <StyledEllipsisTooltip tooltipText={label}>{label}</StyledEllipsisTooltip>
                    {count}
                  </StyledChildren>
                ),
              })),
            }
          : null,
    ),
  ];

  const hasNoValidMetricAlerts = Object.values(metricsCategoryMap).every(
    (categoryMap: any) => !categoryMap.length,
  );
  return {
    sidebarItems: items.filter((i) => !!i),
    hasNoValidMetricAlerts,
    defaultOpenCategories: [
      ...Object.keys(metricsCategoryMap),
      ...Object.keys(metricsCategoryMap['Flow Metrics'] || {}),
    ],
  };
};

export const getDPISidebarConfig = ({ alerts, featureFlags, counts = {} }) => {
  const { isAdAlertEnabled = false } = featureFlags;
  const { ALL_ALERTS, CRITICAL, WARNING, INFO, ACTIVE } = STATIC_SIDEBAR_FILTERS;
  const { Critical, Warning, Info } = SEVERITY_CONFIG;

  // Extract metric counts from the counts object
  const metricCounts = counts['metricCounts'] || {};

  // Use API counts directly
  const criticalCount = counts['critical'] || 0;
  const warningCount = counts['warning'] || 0;
  const infoCount = counts['info'] || 0;
  const activeCount = counts['active'] || 0;
  const allCount = counts['all'] || 0;

  // For metrics categories, use API metric counts to build the category map
  const buildMetricCategoryMapFromCounts = () => {
    if (isAppModule) {
      // DPI metrics categorization using API counts
      const metricAlertsCountMap: any = {
        'Flow Metrics': {},
        'Custom Metrics': [],
        'Standard Metrics': [],
      };
      const { userDefinedMetricsMeta, flowConfigMeta } = metricsStore.getState();
      const customMetrics = userDefinedMetricsMeta[CURRENT_PRODUCT_MODE.toLowerCase()];

      for (const metricKey in metricCounts) {
        const metricAlertsCount = metricCounts[metricKey];
        if (metricAlertsCount) {
          if (customMetrics?.[metricKey]) {
            const flowId = customMetrics[metricKey].flowID;
            if (flowId) {
              const invertFlowMetricMap = _.invert(flowConfigMeta?.[flowId]?.metrics);
              const flowName = flowConfigMeta?.[flowId]?.name;

              metricAlertsCountMap['Flow Metrics'][flowName] = [
                ...(metricAlertsCountMap['Flow Metrics'][flowName] || []),
                {
                  metric: metricKey,
                  count: metricAlertsCount,
                  label: FLOW_METRICS_DISPLAY[invertFlowMetricMap[metricKey]].name,
                },
              ];
            } else {
              metricAlertsCountMap['Custom Metrics'].push({
                metric: metricKey,
                count: metricAlertsCount,
                label: getMetricShortName({ metricKey }),
              });
            }
          } else {
            metricAlertsCountMap['Standard Metrics'].push({
              metric: metricKey,
              count: metricAlertsCount,
              label: getMetricShortName({ metricKey, defaultValue: { short: DELETED_METRIC } }),
            });
          }
        }
      }

      // Sort by count
      Object.keys(metricAlertsCountMap['Flow Metrics']).forEach((key) =>
        metricAlertsCountMap['Flow Metrics'][key].sort((a, b) => b.count - a.count),
      );
      metricAlertsCountMap['Custom Metrics'].sort((a, b) => b.count - a.count);
      metricAlertsCountMap['Standard Metrics'].sort((a, b) => b.count - a.count);

      return metricAlertsCountMap;
    } else {
      // Currently Unused
      // Video metrics categorization using API counts
      const metricAlertsCountMap: any = {
        'Video Metrics': [],
        'Ad Metrics': [],
        'KPI Metrics': [],
      };

      for (const metricKey in metricCounts) {
        const metricAlertsCount = metricCounts[metricKey];
        if (metricAlertsCount) {
          addEiMetricToCategoryMap(
            metricAlertsCountMap,
            metricKey,
            metricAlertsCount,
            isAdAlertEnabled,
          );
        }
      }

      sortVideoMetricCategoryMap(metricAlertsCountMap);

      return metricAlertsCountMap;
    }
  };

  const metricsCategoryMap = buildMetricCategoryMapFromCounts();

  const items: MenuItem[] = [
    {
      key: ALL_ALERTS,
      disabled: !allCount,
      label: (
        <StyledChildren disabled={!allCount}>
          <span>All Alerts</span>
          <span>{allCount}</span>
        </StyledChildren>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: ACTIVE,
      disabled: !activeCount,
      label: (
        <StyledChildren disabled={!activeCount}>
          <span style={{ color: '#215bff' }}>Active Alerts</span>
          {activeCount}
        </StyledChildren>
      ),
    },
    {
      type: 'divider',
    },
    {
      ...Critical,
      key: CRITICAL,
      disabled: !criticalCount,
      label: (
        <StyledChildren disabled={!criticalCount}>
          {Critical.label}
          {criticalCount}
        </StyledChildren>
      ),
    },
    {
      ...Warning,
      key: WARNING,
      disabled: !warningCount,
      label: (
        <StyledChildren disabled={!warningCount}>
          {Warning.label}
          {warningCount}
        </StyledChildren>
      ),
    },
    {
      ...Info,
      key: INFO,
      disabled: !infoCount,
      label: (
        <StyledChildren disabled={!infoCount}>
          {Info.label}
          {infoCount}
        </StyledChildren>
      ),
    },
    {
      type: 'divider',
    },
    ...Object.keys(metricsCategoryMap).map((key) =>
      key === 'Flow Metrics' && !isEmpty(metricsCategoryMap[key])
        ? {
            key: key,
            label: <StyledLabel>{key}</StyledLabel>,
            children: Object.keys(metricsCategoryMap[key]).map((flowKey) => ({
              key: flowKey,
              type: 'group',
              label: (
                <StyledChildren disabled={false}>
                  <StyledEllipsisTooltip tooltipText={flowKey}>{flowKey}</StyledEllipsisTooltip>
                </StyledChildren>
              ),
              children: metricsCategoryMap[key][flowKey].map(({ metric, count, label }) => ({
                key: metric,
                label: (
                  <StyledChildren disabled={!count}>
                    <StyledEllipsisTooltip tooltipText={label}>{label}</StyledEllipsisTooltip>
                    {count}
                  </StyledChildren>
                ),
              })),
            })),
          }
        : metricsCategoryMap[key].length
          ? {
              key: key,
              label: <StyledLabel>{key}</StyledLabel>,
              children: metricsCategoryMap[key].map(({ metric, count, label }) => ({
                key: metric,
                label: (
                  <StyledChildren disabled={!count}>
                    <StyledEllipsisTooltip tooltipText={label}>{label}</StyledEllipsisTooltip>
                    {count}
                  </StyledChildren>
                ),
              })),
            }
          : null,
    ),
  ];

  const hasNoValidMetricAlerts = Object.values(metricsCategoryMap).every(
    (categoryMap: any) => !categoryMap.length,
  );

  return {
    sidebarItems: items.filter((i) => !!i),
    hasNoValidMetricAlerts,
    defaultOpenCategories: [
      ...Object.keys(metricsCategoryMap),
      ...Object.keys(metricsCategoryMap['Flow Metrics'] || {}),
    ],
  };
};
